'use client';

import { useEffect, useState } from 'react';
import { Target, ExternalLink, AlertCircle, TrendingUp, RefreshCw } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import {
  getGunfighterBridgeStatus,
  syncGunfighterBridge,
  type BridgeStatus,
} from '@/lib/gunfighterBridge';
import {
  isBridgeStale,
  getShooterGradeColor,
  getDaysSinceSession,
  type GunfighterBridgeSnapshot,
} from '@/lib/tactiv-bridge';
import { getCrossAppBonusStatus, type CrossAppBonusStatus } from '@/lib/crossAppBonuses';
import { storage } from '@/lib/storage';
import HudPanel from '@/components/ui/HudPanel';

interface GunfighterWidgetProps {
  compact?: boolean;
}

export default function GunfighterWidget({ compact = false }: GunfighterWidgetProps) {
  const { user } = useAuth();
  const [bridgeStatus, setBridgeStatus] = useState<BridgeStatus | null>(null);
  const [bonusStatus, setBonusStatus] = useState<CrossAppBonusStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    async function loadStatus() {
      try {
        const status = await getGunfighterBridgeStatus(user!.uid);
        setBridgeStatus(status);

        if (status.isLinked) {
          const bonus = await getCrossAppBonusStatus(user!.uid);
          setBonusStatus(bonus);
        }
      } catch (error) {
        console.error('Failed to load GunfighterOS status:', error);
      } finally {
        setLoading(false);
      }
    }

    loadStatus();
  }, [user?.uid]);

  const handleSync = async () => {
    if (!user?.uid || syncing) return;

    setSyncing(true);
    try {
      await syncGunfighterBridge(user.uid);
      const status = await getGunfighterBridgeStatus(user.uid);
      setBridgeStatus(status);
    } catch (error) {
      console.error('Failed to sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Don't render if not linked or still loading
  if (loading || !bridgeStatus?.isLinked) {
    return null;
  }

  const snapshot = bridgeStatus.snapshot;
  const isStale = bridgeStatus.isStale;
  const daysSinceRange = snapshot?.lastSession?.date
    ? getDaysSinceSession(snapshot.lastSession.date)
    : -1;
  const gradeStyle = snapshot ? getShooterGradeColor(snapshot.shooterGrade.label) : null;

  // Determine undertrained platforms
  const undertrainedPlatforms = snapshot
    ? Object.entries(snapshot.platforms)
        .filter(([_, data]) => data.score < 40)
        .map(([platform]) => platform)
    : [];

  // Stale degradation — when the last range session is >90 days old (or
  // the bridge flags itself stale), the full stats panel mostly shows
  // zeros and a "log a session" nag would do more for the user than
  // half-truth numbers. Surface a compact CTA instead.
  const isVeryStale = isStale || (daysSinceRange >= 0 && daysSinceRange > 90);
  if (isVeryStale && !compact) {
    return (
      <HudPanel variant="utility" severity="slate" badge="Gunfighter" code="">
        <div className="flex items-start gap-2.5">
          <Target className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] text-slate-300 leading-snug">
              Range time stale — last session{' '}
              <span className="text-amber-300 font-semibold">
                {daysSinceRange > 0 ? `${daysSinceRange} days ago` : 'over 90 days ago'}
              </span>.
              {gradeStyle && snapshot ? ` Grade was ${snapshot.shooterGrade.label} when fresh.` : ''}
            </p>
            <a
              href="https://gunfighteros.app"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-operator-gold hover:text-operator-gold-light transition"
            >
              Log a session <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </HudPanel>
    );
  }

  if (compact) {
    return (
      <div className="bg-operator-gunmetal/40 rounded-lg p-3 border border-operator-steel/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-operator-gold" />
            <span className="text-xs font-semibold uppercase text-gray-400">GunfighterOS</span>
            {isStale && <span className="text-[9px] text-yellow-400">(stale)</span>}
          </div>
          {snapshot && (
            <div className={`text-sm font-bold ${gradeStyle?.text || 'text-gray-400'}`}>
              {snapshot.shooterGrade.label} ({snapshot.shooterGrade.overall})
            </div>
          )}
        </div>
        {snapshot && daysSinceRange >= 0 && (
          <div className="text-[11px] text-gray-500 mt-1">
            Last range: {daysSinceRange === 0 ? 'Today' : `${daysSinceRange} days ago`}
          </div>
        )}
        {!snapshot && (
          <div className="text-[11px] text-gray-500 mt-1">
            No data synced yet
          </div>
        )}
      </div>
    );
  }

  return (
    <HudPanel
      variant="standard"
      severity="gold"
      badge="GUNFIGHTER STATUS"
      code=""
      callout={
        <div className="flex items-center gap-2">
          {isStale && <span className="text-[9px] text-amber-300 uppercase tracking-[0.18em]">stale</span>}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="p-1.5 rounded border border-operator-steel/40 bg-operator-void/40 hover:border-operator-gold/50 transition disabled:opacity-50"
            title="Sync now"
          >
            <RefreshCw className={`w-3 h-3 text-slate-400 ${syncing ? 'animate-spin' : ''}`} />
          </button>
          <a
            href="https://gunfighteros.app"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-operator-gold transition flex items-center gap-1"
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      }
    >
      {snapshot ? (
        <div className="space-y-3">
          {/* Shooting Grade */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">Shooting Grade</div>
            <div className={`flex items-center gap-2 px-2 py-1 rounded ${gradeStyle?.bg || ''}`}>
              <span className={`text-sm font-bold ${gradeStyle?.text || 'text-gray-400'}`}>
                {snapshot.shooterGrade.label}
              </span>
              <span className="text-xs text-gray-400">({snapshot.shooterGrade.overall})</span>
            </div>
          </div>

          {/* Last Range */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">Last Range</div>
            <div className="text-sm text-white">
              {daysSinceRange === 0
                ? 'Today'
                : daysSinceRange === 1
                ? 'Yesterday'
                : daysSinceRange > 0
                ? `${daysSinceRange} days ago`
                : 'Never'}
            </div>
          </div>

          {/* Platform Scores */}
          <div className="bg-operator-gunmetal/40 rounded-lg p-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              {Object.entries(snapshot.platforms).map(([platform, data]) => (
                <div key={platform}>
                  <div className="text-[10px] text-gray-500 uppercase">{platform}</div>
                  <div
                    className={`text-sm font-semibold ${
                      data.score >= 75
                        ? 'text-green-400'
                        : data.score >= 50
                        ? 'text-yellow-400'
                        : data.score >= 25
                        ? 'text-orange-400'
                        : 'text-red-400'
                    }`}
                  >
                    {data.score}
                  </div>
                  <div className="text-[9px] text-gray-600">{data.sessions} sessions</div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div>
              <span className="text-white">{snapshot.totalSessions}</span> total sessions
            </div>
            <div>
              <span className="text-white">{snapshot.totalRounds.toLocaleString()}</span> rounds
            </div>
            <div>
              <span className="text-white">{Math.round(snapshot.averageAccuracy)}%</span> accuracy
            </div>
          </div>

          {/* Undertrained Warning */}
          {undertrainedPlatforms.length > 0 && (
            <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg p-2">
              <AlertCircle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-orange-300">
                <span className="font-semibold capitalize">{undertrainedPlatforms.join(', ')}</span> undertrained.
                Consider range time to improve.
              </div>
            </div>
          )}

          {/* Cross-Training Bonus Status */}
          {bonusStatus?.dualTraining.eligible && (
            <div className="bg-operator-gold/10 border border-operator-gold/30 rounded-lg p-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-operator-gold" />
              <div className="text-xs text-operator-gold">
                +10% Intel bonus active this week!
              </div>
            </div>
          )}

          {/* Dual Week Streak */}
          {bonusStatus && bonusStatus.consistency.consecutiveWeeks > 0 && (
            <div className="text-[11px] text-gray-500 text-center">
              Dual training streak: <span className="text-white font-semibold">{bonusStatus.consistency.consecutiveWeeks} weeks</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400 text-center py-4">
          <Target className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <div>No GunfighterOS data synced yet</div>
          <div className="text-xs text-gray-500 mt-1">
            Complete a range session in GunfighterOS to see your stats here.
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-operator-steel/40 text-xs text-gray-300 hover:bg-operator-steel/60 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      )}
    </HudPanel>
  );
}

/**
 * Cross-sell banner for users who haven't linked GunfighterOS
 * Shows on the OPMODE dashboard to promote GunfighterOS
 */
export function GunfighterCrossSellBanner() {
  const { user } = useAuth();
  const [isLinked, setIsLinked] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;

    async function checkLink() {
      try {
        const status = await getGunfighterBridgeStatus(user!.uid);
        setIsLinked(status.isLinked);
      } catch {
        setIsLinked(false);
      }
    }

    // Check localStorage for dismissal
    const dismissedKey = `gunfighter_crosssell_dismissed_${user.uid}`;
    if (storage.get(dismissedKey) === 'true') {
      setDismissed(true);
      return;
    }

    checkLink();
  }, [user?.uid]);

  const handleDismiss = () => {
    if (user?.uid) {
      storage.set(`gunfighter_crosssell_dismissed_${user.uid}`, 'true');
    }
    setDismissed(true);
  };

  // Don't show if linked, loading, or dismissed
  if (isLinked !== false || dismissed) {
    return null;
  }

  return (
    <HudPanel
      variant="standard"
      severity="emerald"
      badge="COMPLETE YOUR TACTICAL TRAINING"
      code=""
      callout={
        <button
          onClick={handleDismiss}
          className="text-slate-500 hover:text-slate-300 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      }
    >
      <div className="flex items-start gap-3">
        <Target className="w-8 h-8 text-emerald-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-xs text-gray-400 mb-3">
            Add GunfighterOS to track your shooting skills and earn cross-training bonuses.
            Link accounts for <span className="text-operator-gold">+500 Intel</span> instantly.
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href="https://gunfighteros.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-operator-gold/20 border border-operator-gold/40 text-operator-gold text-xs font-semibold hover:bg-operator-gold/30 transition"
            >
              <Target className="w-3.5 h-3.5" />
              Get GunfighterOS
              <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="/settings"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-operator-steel/40 text-gray-300 text-xs font-semibold hover:bg-operator-steel/60 transition"
            >
              Link Existing Account
            </a>
          </div>
        </div>
      </div>
    </HudPanel>
  );
}
